import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalStorage } from './useLocalStorage.js';

export interface ColumnDef {
  key: string;
  label: string;
  defaultWidth: number;
  minWidth?: number;
}

/**
 * Generalizes the mouse-driven resize pattern already used for the
 * Execution Logs list/detail split (mousedown → isResizing → window
 * mousemove/mouseup → clamped value), parameterized per-column and
 * persisted via the same useLocalStorage hook the split width already uses.
 * Columns resize by pixel delta rather than percent-of-container, since
 * each column has its own fixed width instead of sharing one axis.
 */
export function useResizableColumns(
  columns: ColumnDef[],
  storageKey: string
): {
  widths: Record<string, number>;
  startResize: (columnKey: string, e: React.MouseEvent) => void;
} {
  const defaults = useMemo(() => {
    const w: Record<string, number> = {};
    for (const c of columns) w[c.key] = c.defaultWidth;
    return w;
  }, [columns]);

  const [widths, setWidths] = useLocalStorage<Record<string, number>>(storageKey, defaults);
  const [resizingKey, setResizingKey] = useState<string | null>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const startResize = useCallback(
    (columnKey: string, e: React.MouseEvent) => {
      e.preventDefault();
      const col = columns.find((c) => c.key === columnKey);
      startXRef.current = e.clientX;
      startWidthRef.current = widths[columnKey] ?? col?.defaultWidth ?? 100;
      setResizingKey(columnKey);
    },
    [columns, widths]
  );

  useEffect(() => {
    if (!resizingKey) return;
    const col = columns.find((c) => c.key === resizingKey);
    const minWidth = col?.minWidth ?? 30;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startXRef.current;
      const next = Math.max(minWidth, startWidthRef.current + delta);
      setWidths((prev) => ({ ...prev, [resizingKey]: next }));
    };
    const handleMouseUp = () => setResizingKey(null);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingKey, columns, setWidths]);

  return { widths, startResize };
}
